import React from "react";
import styled, { keyframes } from "styled-components";
import { themes } from "../utils/Themes";

// Get initial theme from localStorage for loader (renders before ThemeProvider)
const getLoaderTheme = () => {
  try {
    const mode = localStorage.getItem("portfolio_theme") || "dark";
    return themes[mode] || themes.dark;
  } catch {
    return themes.dark;
  }
};

const loaderTheme = getLoaderTheme();

// Simple working shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Pulsate animation
const pulsate = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Gradient background animation
const gradientBG = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Main loader wrapper - this handles the full screen
const LoaderWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: linear-gradient(135deg, ${loaderTheme.bg}, ${loaderTheme.card}, ${loaderTheme.card_light}, ${loaderTheme.bg});
  background-size: 400% 400%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  z-index: 9999;
  animation: ${gradientBG} 10s ease infinite;
`;

// Logo image
const LogoImage = styled.img`
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
  border-radius: 50%;
  object-fit: cover;
  animation: ${pulsate} 3s ease-in-out infinite;
  box-shadow: 0 0 30px rgba(${loaderTheme.primaryRgb}, 0.3);

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

// Shimmer text with working effect
const ShimmerText = styled.h1`
  font-size: 6rem;
  font-weight: 700;
  text-transform: uppercase;
  background: linear-gradient(
    90deg,
    ${loaderTheme.primary} 0%,
    #ffffff 25%,
    ${loaderTheme.primary} 50%,
    #ffffff 75%,
    ${loaderTheme.primary} 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 2s linear infinite;

  /* Fallback for browsers that don't support background-clip */
  @supports not (-webkit-background-clip: text) {
    background: none;
    color: ${loaderTheme.primary};
    text-shadow: 0 0 20px ${loaderTheme.primary};
    animation: ${pulsate} 3s ease-in-out infinite;
  }

  @media (max-width: 768px) {
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

// Dots animation
const dotBounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0.5);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const LoaderDots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
`;

const Dot = styled.div`
  width: 15px;
  height: 15px;
  margin: 0 8px;
  background-color: ${loaderTheme.primary};
  border-radius: 50%;
  animation: ${dotBounce} 1.4s infinite ease-in-out both;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

// Tech stack reveal
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TechStack = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 40px;
  opacity: 0;
  animation: ${fadeInUp} 1s ease-out forwards;
  animation-delay: 1s;
`;

const TechItem = styled.span`
  margin: 5px 15px;
  font-size: 1.1rem;
  color: ${loaderTheme.text_secondary};
  opacity: 0.8;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    color: ${loaderTheme.primary};
    opacity: 1;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 3px 10px;
  }
`;

const SubtitleText = styled.p`
  margin-top: 30px;
  color: ${loaderTheme.text_secondary};
  font-size: 1.4rem;
  opacity: 0.9;
  letter-spacing: 2px;
  text-align: center;
  animation: ${fadeInUp} 1s ease-out forwards;
  animation-delay: 1.5s;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    letter-spacing: 1px;
  }
`;

const GlobalLoader = () => {
  return (
    <LoaderWrapper>
      <LogoImage src="/z1.jpeg" alt="Logo" />
      <ShimmerText>Zafran Khan</ShimmerText>
      <LoaderDots>
        <Dot />
        <Dot />
        <Dot />
      </LoaderDots>
      <TechStack>
        <TechItem>React</TechItem>
        <TechItem>TypeScript</TechItem>
        <TechItem>Styled Components</TechItem>
      </TechStack>
      <SubtitleText>Welcome to My Portfolio</SubtitleText>
    </LoaderWrapper>
  );
};

export default GlobalLoader;
